'use client';

import React from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { usePortfolioData } from '@/context/PortfolioDataContext';
import AppImage from '@/components/ui/AppImage';

export default function ProjectsSection() {
  const { t } = useLanguage();
  const { projects, hero, contact } = usePortfolioData();

  const displayProjects =
    projects?.map((p) => ({
      title: p.title,
      description: p.description,
      tech: p.tech_stack || [],
      status: p.status === 'active' ? t?.projects_status_active : t?.projects_status_done,
      statusColor: p.status === 'active' ? '#22c55e' : '#63b3ed',
      liveUrl: p.live_url || '',
      githubUrl: p.github_url || '',
      imageUrl: p.image_url || '',
      featured: p.featured,
    })) || [];

  const linkedInUrl = hero?.linkedin_url || contact?.linkedin_url || '';

  return (
    <section id="projects" className="relative py-28 px-6 overflow-hidden">
      <div
        className="absolute inset-0 z-0"
        style={{ background: 'linear-gradient(180deg, #141c28 0%, #1B2432 100%)' }}
      />
      <div
        className="absolute right-0 top-1/3 pointer-events-none z-0"
        style={{
          width: 500,
          height: 600,
          background:
            'radial-gradient(ellipse at right, rgba(167,139,250,0.06) 0%, transparent 70%)',
        }}
      />
      <div className="relative z-10 max-w-5xl mx-auto">
        <div className="reveal flex items-center gap-3 mb-4">
          <div
            className="h-px flex-1 max-w-[48px]"
            style={{ background: 'rgba(99,179,237,0.5)' }}
          />
          <span
            className="text-xs font-semibold uppercase tracking-[0.22em]"
            style={{ color: '#63b3ed' }}
          >
            {t?.projects_label}
          </span>
        </div>

        <div className="reveal flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14 reveal-delay-1">
          <h2
            className="font-display font-bold"
            style={{
              fontSize: 'clamp(2rem, 5vw, 3.25rem)',
              color: '#EDF2F7',
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
            }}
          >
            {t?.projects_heading_1}{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, #63b3ed, #a78bfa)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {t?.projects_heading_2}
            </span>
          </h2>
          {linkedInUrl ? (
            <a
              href={linkedInUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium flex items-center gap-1.5 transition-colors duration-200"
              style={{ color: 'rgba(99,179,237,0.7)' }}
            >
              {t?.projects_view_linkedin}
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M3 7h8M7 3l4 4-4 4"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
          ) : null}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {displayProjects?.map((project, i) => (
            <div
              key={i}
              className={`reveal reveal-delay-${i + 1} glow-card rounded-2xl p-7 flex flex-col ${project?.featured ? 'md:col-span-1' : ''}`}
              style={{
                background: project?.featured ? 'rgba(99,179,237,0.04)' : 'rgba(255,255,255,0.025)',
              }}
            >
              {project?.imageUrl ? (
                <div
                  className="w-full rounded-xl overflow-hidden mb-5"
                  style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(200,206,214,0.08)',
                  }}
                >
                  <AppImage
                    src={project.imageUrl}
                    alt={project.title}
                    width={1200}
                    height={600}
                    className="w-full h-[180px] object-cover"
                  />
                </div>
              ) : null}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <h3 className="font-display font-bold text-xl" style={{ color: '#EDF2F7' }}>
                    {project?.title}
                  </h3>
                  {project?.featured && (
                    <span
                      className="text-xs font-bold px-2 py-0.5 rounded-full"
                      style={{
                        background: 'rgba(167,139,250,0.15)',
                        border: '1px solid rgba(167,139,250,0.3)',
                        color: '#a78bfa',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {t?.projects_featured}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {project?.githubUrl ? (
                    <a
                      href={project.githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center w-9 h-9 rounded-xl transition-all duration-200 hover:scale-105"
                      style={{
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(200,206,214,0.1)',
                        color: 'rgba(237,242,247,0.6)',
                      }}
                      aria-label="GitHub"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M9 19c-4 1.5-4-2.5-5-3m10 6v-3.87c0-1.1.1-1.9-.4-2.6 1.7-.2 3.5-.8 3.5-3.9 0-.9-.3-1.6-.8-2.2.1-.2.3-1-.1-2.1 0 0-.7-.2-2.3.8-.7-.2-1.4-.3-2.1-.3-.7 0-1.4.1-2.1.3-1.6-1-2.3-.8-2.3-.8-.4 1.1-.2 1.9-.1 2.1-.5.6-.8 1.3-.8 2.2 0 3.1 1.8 3.7 3.5 3.9-.3.3-.5.7-.5 1.4v3.1"
                          stroke="currentColor"
                          strokeWidth="1.4"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </a>
                  ) : null}
                  {project?.liveUrl ? (
                    <a
                      href={project.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center w-9 h-9 rounded-xl transition-all duration-200 hover:scale-105"
                      style={{
                        background: 'rgba(99,179,237,0.08)',
                        border: '1px solid rgba(99,179,237,0.2)',
                        color: '#63b3ed',
                      }}
                      aria-label="Live"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M14 3h7v7"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M10 14L21 3"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M21 14v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h6"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </a>
                  ) : null}
                </div>
              </div>
              <p
                className="text-sm leading-relaxed mb-6 flex-1"
                style={{ color: 'rgba(237,242,247,0.55)' }}
              >
                {project?.description}
              </p>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex flex-wrap gap-1.5">
                  {project?.tech?.map((tech: string) => (
                    <span
                      key={tech}
                      className="text-xs px-2.5 py-1 rounded-lg font-medium"
                      style={{
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(200,206,214,0.1)',
                        color: 'rgba(237,242,247,0.55)',
                      }}
                    >
                      {tech}
                    </span>
                  ))}
                </div>
                <span
                  className="flex items-center gap-1.5 text-xs font-semibold"
                  style={{ color: project?.statusColor }}
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: project?.statusColor }}
                  />
                  {project?.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div
        className="absolute bottom-0 left-0 right-0 h-px"
        style={{
          background: 'linear-gradient(90deg, transparent, rgba(200,206,214,0.12), transparent)',
        }}
      />
    </section>
  );
}
